import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { styles } from 'app/theme'
import { useParallax } from 'app/utils/customFunctions'
import { useScroll } from 'framer-motion'
import React, { memo, useRef } from 'react'

import { Item, Props as ItemProps } from './Item'

export interface Props {
  claim?: string
  items: ItemProps[]
  label?: string
}

export const ExperiencesPreview = memo(function ExperiencesPreview({
  claim,
  items,
  label,
}: Props) {
  if (!items) {
    return null
  }

  if (items.length < 1) {
    return null
  }

  const containerRef = useRef<any>()

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', '100vh'],
  })

  const y = useParallax(scrollYProgress, [-50, 50])

  return (
    <Container>
      {label ? <Label tag="h2" text={label} /> : null}

      {claim ? <Claim text={claim} /> : null}

      <Wrapper ref={containerRef}>
        {items.map((item, index) => (
          <Item key={index} index={index + 1} style={y} {...item} />
        ))}
      </Wrapper>
    </Container>
  )
})

const Container = styled.section`
  padding: 9.75rem 5vw 4.5rem;
  position: relative;
  text-align: center;
  &:before {
    content: '';
    width: 100%;
    height: 100%;
    background: url('/background-dark-01.webp') no-repeat top center;
    background-size: cover;
    position: absolute;
    top: 0;
    left: 0;
  }

  @media (max-width: 1199px) {
    padding: 5.25rem 2rem 2rem;
  }
`

const Label = styled(AnimatedTitle)`
  ${styles.label}

  color: ${({ theme }) => theme.colors.variants.neutralLight1};
`

const Claim = styled(AnimatedTitle)`
  ${styles.claim}

  max-width: 59rem;
  color: ${({ theme }) => theme.colors.variants.neutralLight1};
  margin: auto;

  @media (max-width: 1199px) {
    max-width: none;
  }
`

const Wrapper = styled.div`
  display: grid;
  gap: 3rem;
  grid-template-columns: repeat(2, 1fr);
  margin-top: 5.25rem;

  @media (max-width: 1199px) {
    display: block;
    margin-top: 3.75rem;
  }
`
